import type { ContactUnitLinks } from "../api/crm";
import type { LinkType } from "./link-variable-utils";
import { linkForType } from "./link-variable-shared";

export const EMAIL_LINK_KEYS: LinkType[] = [
  "lead.application_link",
  "lead.request_showing_link"
];

const isLinkType = (key: string): key is LinkType =>
  (EMAIL_LINK_KEYS as string[]).includes(key);

export const shortUrlForUnit = (
  unit: ContactUnitLinks | undefined,
  key: LinkType
): string | null => (unit ? (linkForType(unit, key)?.short_url ?? null) : null);

interface MailyJsonNode {
  type?: string;
  attrs?: { id?: string | null };
  content?: MailyJsonNode[];
}

// Collect the distinct link-variable keys present in a Maily JSON doc.
const collectFromJson = (
  node: MailyJsonNode | null | undefined,
  found: Set<LinkType>
): void => {
  if (!node) return;
  const id = node.attrs?.id;
  if (node.type === "variable" && id && isLinkType(id)) found.add(id);
  (node.content ?? []).forEach((child) => collectFromJson(child, found));
};

// eslint-disable-next-line sonarjs/slow-regex -- bounded: only the two known keys, optional fallback
const buildTokenRegex = (key: LinkType): RegExp =>
  new RegExp(
    `\\{\\{${key.replace(/\./g, "\\.")}(?:,fallback=[^}]*)?\\}\\}`,
    "g"
  );

// Link-variable keys present either in the Maily JSON doc or as raw
// `{{key}}` tokens in the HTML body (covers the HTML-editor path and
// template-seeded bodies).
export const collectLinkVariableKeys = (args: {
  mailyJson?: Record<string, unknown> | null;
  htmlBody?: string;
}): LinkType[] => {
  const found = new Set<LinkType>();
  collectFromJson(args.mailyJson as MailyJsonNode | null, found);
  const html = args.htmlBody;
  if (html)
    EMAIL_LINK_KEYS.forEach((key) => {
      // Fresh regex each call -> no stateful lastIndex carry-over.
      if (buildTokenRegex(key).test(html)) found.add(key);
    });
  return EMAIL_LINK_KEYS.filter((key) => found.has(key));
};

// Replace every `{{link_key}}` (or `{{link_key,fallback=...}}`) token with
// the chosen unit's literal short URL. Name-variable tokens are left intact
// for the server to resolve at send. Used on the rendered HTML and the
// derived plain text before send.
export const replaceLinkTokens = (
  text: string,
  selections: Record<LinkType, ContactUnitLinks | undefined>
): string => {
  let out = text;
  EMAIL_LINK_KEYS.forEach((key) => {
    const url = shortUrlForUnit(selections[key], key);
    if (url) out = out.replace(buildTokenRegex(key), url);
  });
  return out;
};
